import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import { Section, SectionHead, card, orb, pill } from "../../components/section";
import { usePublicDirectory } from "../../hooks/use-public-directory";
import { contactOf, useSiteConfig } from "../../hooks/use-site-config";
import type { SlotPick } from "../../types";
import { reachOutLabel } from "../../utils/labels";
import { SlotGateDialog } from "./slot-gate-dialog";
import { placeName } from "../../utils/directory";
import { CoachRow, dayLabel, row } from "./week-grid";

const DAY_MS = 24 * 60 * 60 * 1000;

/** The first day of the grid `weeks` pages ahead, at the visitor's local midnight. */
const weekStart = (weeks: number, days: number) => {
  if (weeks === 0) return undefined;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return new Date(start.getTime() + weeks * days * DAY_MS).toISOString();
};
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The open calendar: every coach with their free times for the week, readable without an
 * account. Tapping a time opens the sign-in step; the service pills and the week arrows refetch
 * the same public endpoint, so what a visitor sees is what the booking pack would offer them.
 */
export const OpenCalendar = () => {
  const site = useSiteConfig();
  const days = site.calendarDays;
  const [weeks, setWeeks] = useState(0);
  const [sessionTypeId, setSessionTypeId] = useState<string>();
  const [pick, setPick] = useState<SlotPick | null>(null);
  const { directory, loading, unavailable } = usePublicDirectory({ from: weekStart(weeks, days), days, sessionTypeId });

  const specialists = directory?.specialists ?? [];
  const serviceName = (id: string | null) => directory?.services.find((service) => service.id === id)?.name ?? null;
  // With one place every time is there; with several each day says which.
  const places = directory?.places ?? [];
  const placeOf =
    places.length > 1
      ? (locationId: string | null | undefined) => {
          const place = places.find((candidate) => candidate.id === locationId);
          return place ? placeName(place) : null;
        }
      : undefined;
  const picked = specialists.find((specialist) => specialist.id === pick?.specialistId) ?? null;
  const header = specialists[0]?.days ?? [];
  const contact = contactOf(site);

  return (
    <Section id="times">
      <SectionHead title={m.landing_calendar_title()} aside={m.landing_calendar_aside()} />
      <div
        className={cn(card, "grid gap-2 p-3 md:p-4")}
        style={{ "--days": header.length || days } as React.CSSProperties}
      >
        {/* The service pills scroll sideways, because a studio can have any number of services
            and a wrapping pile of pills would push the week arrows into a corner. */}
        {directory ? (
          <div className="flex items-center gap-2 p-1">
            <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                type="button"
                data-tour="calendar-filter"
                className={pill}
                aria-pressed={!sessionTypeId}
                onClick={() => setSessionTypeId(undefined)}
              >
                {m.landing_calendar_everyone()}
              </button>
              {directory.services.map((service) => (
                <button
                  key={service.id}
                  type="button"
                  data-tour="calendar-filter"
                  className={pill}
                  aria-pressed={sessionTypeId === service.id}
                  onClick={() => setSessionTypeId(service.id)}
                >
                  {service.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-label={m.landing_calendar_earlier()}
              disabled={weeks === 0}
              className={cn(orb, "size-[38px] disabled:opacity-30")}
              onClick={() => setWeeks((current) => Math.max(0, current - 1))}
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label={m.landing_calendar_later()}
              className={cn(orb, "size-[38px]")}
              onClick={() => setWeeks((current) => current + 1)}
            >
              <ChevronRight className="size-4" />
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="grid gap-2">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-24 rounded-[20px]" />
            ))}
          </div>
        ) : null}

        {!loading && (unavailable || specialists.length === 0) ? (
          <div className="px-6 py-16 text-center">
            <p className="text-lg font-medium tracking-[-0.01em]">{m.landing_calendar_empty_title()}</p>
            <p className="mt-1 text-muted-foreground">{reachOutLabel(contact)}</p>
            {contact ? (
              <a href={contact.href} className="mt-5 inline-block text-2xl font-medium tracking-[-0.02em]">
                {contact.label}
              </a>
            ) : null}
          </div>
        ) : null}

        {!loading && specialists.length > 0 ? (
          <>
            <div className={cn(row, "hidden items-center py-2 text-[13px] text-muted-foreground lg:grid")}>
              <span>{m.landing_calendar_count({ count: specialists.length })}</span>
              {header.map((day, index) => {
                const label = dayLabel(day.date, index, weeks);
                return (
                  <span key={day.date} className="text-center">
                    <b
                      className={cn(
                        "mr-1.5 font-semibold text-foreground",
                        index === 0 && weeks === 0 && "rounded-full bg-primary px-2 py-0.5 text-primary-foreground",
                      )}
                    >
                      {label.number}
                    </b>
                    {label.name}
                  </span>
                );
              })}
            </div>
            {specialists.map((specialist) => (
              <CoachRow
                key={specialist.id}
                specialist={specialist}
                serviceName={serviceName(specialist.serviceId)}
                placeOf={placeOf}
                onPick={setPick}
              />
            ))}
          </>
        ) : null}
      </div>

      <SlotGateDialog
        pick={pick}
        specialist={picked}
        serviceName={serviceName(pick?.serviceId ?? null)}
        onClose={() => setPick(null)}
      />
    </Section>
  );
};
